import JournalTextArticlePage from './JournalTextArticlePage'

export const SOCCER_FASHION_PATH = '/the-global-influence-of-soccer-and-fashion'

export default function GlobalInfluenceSoccerFashion() {
  return (
    <JournalTextArticlePage
      title="The Global Influence of Soccer & Fashion"
      titleFont='"Helvetica Neue", Helvetica, Arial, sans-serif'
    >
      <p>
        Sports &amp; Fashion. These two worlds couldn&apos;t seem further apart. One is catching
        the game Sunday night; the other is catching who&apos;s best dressed at an award show
        Sunday night. One enjoys player stats; the other enjoys fashion show reviews. One enjoys
        dry fit; the other wouldn&apos;t dare. Throughout all their differences, the similarities
        between the two are undeniable. The athleticism in sports inspires on lookers by showcasing
        tremendous strength &amp; agility to perform on an intense stage. Fashion inspires on
        lookers by creating designs to invoke thought through the body artform also performed on an
        intense stage. In recent years, we are seeing the influence of sports impact fashion
        globally. Whether you&apos;re watching your favorite rookie&apos;s Draft Day fit, seeing
        fit pics drop on the TL as athletes walk out to the locker room, or catching star players
        switching courtside for runway seats; these two worlds aren&apos;t as far off as we think.
        So far in 2024, we have already seen many collaborations between soccer clubs, athletes,
        &amp; fashion houses combining different aspects of their worlds to redefine the
        relationship between the two.
      </p>

      <div style={{ textAlign: 'center', margin: '24px 0' }}>
        <img
          src="/burberry-show.png"
          alt="Martin Ødegaard and Bukayo Saka at Burberry AW24"
          style={{ maxWidth: '100%', width: 'min(420px, 100%)', height: 'auto', display: 'inline-block' }}
        />
      </div>

      <p><strong>Front Row at Fashion Shows</strong></p>

      <p>
        &ldquo;We express ourselves on the pitch, and now we feel comfortable enough to express
        ourselves off it.&rdquo; Arsenal&apos;s forward/midfielder, Bukayo Saka, told British GQ
        earlier this year at the Burberry&apos;s AW24 show during London Fashion Week. Alongside
        fellow midfielder &amp; Arsenal captain, Martin Ødegaard, the two had a night off the
        pitch to enjoy the newest work from Daniel Lee &amp; British heritage. Footballers
        partaking in fashion week was a change of pace for many. Another example of a change of
        cleats for loafers could be seen during Milan Fashion Week when Liverpool defender, Trent
        Alexander-Arnold, stepped out to spectate at Prada&apos;s menswear show in January. Over
        at Paris Fashion Week, Paris&apos; own club athletes were out at the crème de la crème of
        French houses. Paris Saint-Germain forward, Hugo Ekitike, was front row during
        Pharrell&apos;s second Louis Vuitton&apos;s menswear show. Some examples of how life off
        the pitch for footballers doesn&apos;t have to be limited to sports related activities.
        That you could possibly have more in common with your favorite athlete than whatever club
        you rep.
      </p>

      <div style={{ textAlign: 'center', margin: '24px 0' }}>
        <img
          src="/y3-realmadrid.png"
          alt="Y-3 x Real Madrid campaign"
          style={{ maxWidth: '100%', width: 'min(420px, 100%)', height: 'auto', display: 'inline-block' }}
        />
      </div>

      <p><strong>Real Madrid&apos;s Real Style</strong></p>

      <p>
        Collaborations can be seen all throughout fashion. Supreme x Maison Margiela. Balenciaga x
        Gucci. Louis Vuitton x Tyler, The Creator. But when Yohji Yamamoto &amp; Adidas&apos; Y3
        meet Real Madrid once again to collaborate on a new match wear collection, it&apos;s bound
        to get attention. Combining elegance, tradition, &amp; peak sportswear, the collection
        highlights the powerhouse identities of both collaborators. The match wear kits are for
        both the Real Madrid Men&apos;s &amp; Women&apos;s teams, &ldquo;constructed with moisture
        wicking HEAT.RDY technology to keep players feeling dry while performing on the biggest
        stage.&rdquo; Designed in the club&apos;s DNA shade, purple, with the signature rose
        graphic that Yamamoto incorporates in his Adidas works. The campaign was shot by London
        photographer, Gabriel Moses, featuring some of Real Madrid&apos;s greats including Jude
        Bellingham, David Alaba, Kathellen Sousa, Naomie Feller, &amp; Zinedine Zidane. This
        great collab has already been seen on the pitch just 5 days after they went on sale to the
        public on March 27th &amp; is already such a success. As stylish as Real Madrid is during
        matches, they are also sure to look as good traveling to them as well. Earlier this month,
        men&apos;s luxury brand, Zegna, announced that they are the official travel-wear partner
        of Real Madrid. This exclusive collection &ldquo;is crafted from fine materials then
        hallmarked with the iconic 232 Road Brand Mark. The partnership expresses Real Madrid and
        ZEGNA&apos;s shared values as the world&apos;s biggest club and leading menswear
        brand.&rdquo; Ensuring that &ldquo;every king needs a tailor&rdquo;, Zegna has made this
        collection available at select stores worldwide with a Made to Measure service to create a
        wardrobe that focuses on quality &amp; complimenting the unique physique of the wearer.
        The team has already been spotted rocking this collection upon arrival for their UCL match
        against Manchester City on April 16th. The lads were truly dressed for the successful
        occasion.
      </p>

      <div style={{ textAlign: 'center', margin: '24px 0' }}>
        <img
          src="/gq-cover.png"
          alt="GQ cover"
          style={{ maxWidth: '100%', width: 'min(420px, 100%)', height: 'auto', display: 'inline-block' }}
        />
      </div>

      <p><strong>Featured Footballers</strong></p>

      <p>
        Fashion allows the individual to indulge &amp; express themselves freely. That&apos;s no
        different for athletes. Seeing your favorite footballer being featured in campaigns &amp;
        editorials for your favorite brand brings a dream to life that you didn&apos;t know you
        needed. Like when your favorite artists from two different genres collaborate; it&apos;s
        not technically supposed to happen, but it sounds so good. That&apos;s how it felt when
        French national team captain &amp; PSG striker, Kylian Mbappé, graced the cover of
        January&apos;s French GQ with guest editor &amp; designer, Kim Jones, in charge of the
        edition. Mbappé having the opportunity to use the GQ platform to discuss his life as a
        footballer, including the frustrations &amp; successes that come with that is a refreshing
        change of speed for the typical GQ reader.
      </p>

      <div style={{ textAlign: 'center', margin: '24px 0' }}>
        <img
          src="/vogue-vandijk.png"
          alt="Vogue Scandinavia editorial"
          style={{ maxWidth: '100%', width: 'min(420px, 100%)', height: 'auto', display: 'inline-block' }}
        />
      </div>

      <p>
        Another editorial moment is from February in Vogue Scandinavia&apos;s feature of Liverpool
        defender &amp; captain, Virgil van Dijk, modeling the latest collection of Danish brand
        Rains. As a longtime supporter and friend of cofounders, Philip Lotko &amp; Daniel Brix,
        Dijk was the obvious muse to market the universe of outwear concepts they are creating.
        Using athletes to model who precisely fit the brand&apos;s ethos is what audiences want to
        see. Not just a buying gimmick but someone who lives true to the brand itself. We see this
        in Adidas Berlin&apos;s latest campaign starring Bayern Munich forward/midfielder, Serge
        Gnabry. Illustrating Gnabry capturing the different moments in his day through the
        different options of Adidas Originals. The campaign accommodates his personal style well
        while remaining true to the brand. Campaigns, features, &amp; editorials like this allow
        you to build upon your relationship with the clothes &amp; athletes you admire. Acting as a
        common ground that the audience &amp; sport share.
      </p>

      <p>
        Sports &amp; Fashion. Though seeming to be two worlds far apart, the commonalities cross
        over seamlessly. Allowing athletes to explore creativity through an invoking art form.
        While fashion is allowed to study the human form being tested at tremendous levels. Both
        stages shine bright, shaping the history &amp; future culture of each other&apos;s
        industries. Looking forward to what collaboration is to come next in 2024.
      </p>

      <div style={{ textAlign: 'center', margin: '32px 0 0' }}>
        <img
          src="/The%20Global%20Influence%20of%20Soccer%20%26%20Fashion.jpg"
          alt="The Global Influence of Soccer & Fashion"
          style={{ maxWidth: '100%', width: 'min(420px, 100%)', height: 'auto', display: 'inline-block' }}
        />
      </div>
    </JournalTextArticlePage>
  )
}
